/**
 * Lightweight DOM helpers. Nothing fancy — hyperscript-like `h()` for readable
 * component code without a framework.
 */

type Child = Node | string | number | false | null | undefined;

type EventListenerMap = {
  [K in keyof HTMLElementEventMap]?: (ev: HTMLElementEventMap[K]) => void;
};

export interface ElementProps {
  class?: string;
  id?: string;
  role?: string;
  tabindex?: number | string;
  href?: string;
  type?: string;
  name?: string;
  disabled?: boolean;
  checked?: boolean;
  readonly?: boolean;
  required?: boolean;
  value?: string | number;
  placeholder?: string;
  min?: string | number;
  max?: string | number;
  maxlength?: number;
  inputmode?: string;
  autocomplete?: string;
  for?: string;
  title?: string;
  style?: string;
  text?: string;
  html?: string;
  dataset?: Record<string, string>;
  aria?: Record<string, string | boolean | number>;
  on?: EventListenerMap;
  [key: string]: unknown;
}

export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props?: ElementProps | null,
  children?: Child | Child[],
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  if (props) {
    for (const [key, value] of Object.entries(props)) {
      if (value == null || value === false) continue;
      if (key === 'class') {
        el.className = String(value);
      } else if (key === 'text') {
        el.textContent = String(value);
      } else if (key === 'html') {
        el.innerHTML = String(value);
      } else if (key === 'dataset' && typeof value === 'object') {
        for (const [dk, dv] of Object.entries(value as Record<string, string>)) {
          el.dataset[dk] = dv;
        }
      } else if (key === 'aria' && typeof value === 'object') {
        for (const [ak, av] of Object.entries(value as Record<string, unknown>)) {
          el.setAttribute(`aria-${ak}`, String(av));
        }
      } else if (key === 'on' && typeof value === 'object') {
        for (const [ev, fn] of Object.entries(value as EventListenerMap)) {
          if (typeof fn === 'function') {
            el.addEventListener(ev, fn as EventListener);
          }
        }
      } else if (key === 'style') {
        el.setAttribute('style', String(value));
      } else if (key in el) {
        try {
          (el as unknown as Record<string, unknown>)[key] = value;
        } catch {
          el.setAttribute(key, String(value));
        }
      } else {
        el.setAttribute(key, String(value));
      }
    }
  }
  appendChildren(el, children);
  return el;
}

export function appendChildren(parent: Node, children: Child | Child[] | undefined): void {
  if (children == null || children === false) return;
  if (Array.isArray(children)) {
    for (const c of children) appendChildren(parent, c);
    return;
  }
  if (typeof children === 'string' || typeof children === 'number') {
    parent.appendChild(document.createTextNode(String(children)));
    return;
  }
  parent.appendChild(children);
}

export function clear(el: Element): void {
  while (el.firstChild) el.removeChild(el.firstChild);
}

export function mount(root: Element, node: Node): void {
  clear(root);
  root.appendChild(node);
}
